import { useState, useEffect, useMemo } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import Button from "@atlaskit/button";
import ProgressBar from "@atlaskit/progress-bar";
import { token } from "@atlaskit/tokens";
import { USER_SEGMENTATION_TEAM_TYPE_SOFTWARE } from "@atlassian/onboarding-survey";
import { MadlibSetupPrompt } from "@atlassian/product-setup";

import { useCloudStatus } from "../../../../../hooks";
import { StepContext, StepProps } from "../../../types";

import { stepMessages } from "./messages";
import { useSuggestionsForTeamType } from "./suggestions";

export type MadlibStepContext = {
    data?: object;
} & StepContext;

export const Madlib = <T extends StepContext>({
    context,
    setContext,
    nextStep
}: StepProps<T>) => {
    const intl = useIntl();
    const { createCloudStatus } = useCloudStatus();
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [projectSetupComplete, setProjectSetupComplete] = useState(false);

    const [header, setHeader] = useState(
        intl.formatMessage(stepMessages.header)
    );
    const [subheader, setSubheader] = useState(
        intl.formatMessage(stepMessages.subheader)
    );

    const { teamType = USER_SEGMENTATION_TEAM_TYPE_SOFTWARE } =
        context as MadlibStepContext;

    /*
     * TODO: Set projectId for invite api interceptor after project is successfully created.
     * const { setProjectId: setProjectIdForInviteApiInterceptor } = useJfind1663TaskPolling();
     */

    const suggestions = useSuggestionsForTeamType(teamType);

    const { defaultValue } = useMemo(() => {
        const [suggestion] = suggestions;
        if (!suggestion) {
            return {
                defaultValue: {
                    task: "",
                    goal: "",
                    dueDate: new Date().getTime()
                }
            };
        }

        const defaultValue = suggestion.data;

        return { defaultValue };
    }, [suggestions]);

    const onSubmit = async (data: object | undefined) => {
        // set request in in progress to show loading state
        setFormSubmitted(true);

        setContext({
            ...context,
            data
        });
    };

    useEffect(() => {
        if (!formSubmitted || createCloudStatus !== "success") {
            return;
        }

        /*
         * TODO: Call Content Generation API to generate project issues based on template, team type, and madlib data
         *
         * const issues = await generateProjectIssues({
         *    template,
         *    teamType,
         *    madlibData: data
         * });
         */

        /*
         * TODO: Call Onboarding API to create project with generated issues.
         * const taskId = await createProjectWithIssues(issues);
         */

        /* TODO: Store taskId to pass to product redirect url  */
        const taskId = "generated-task-id";
        setContext((prevContext) => ({
            ...prevContext,
            taskId
        }));

        /*
         * TODO: Wait for project creation to complete before proceeding to next step
         * await waitForProjectCreationToComplete(taskId);
         */

        /*
         * TODO: set project id for project polling
         * setProjectIdForInviteApiInterceptor('generated-project-id');
         */

        /* TODO: Remove timeout */
        setTimeout(() => {
            setProjectSetupComplete(true);
        }, 10_000);
    }, [createCloudStatus, formSubmitted, setContext]);

    // update header and subheader when form is submitted
    useEffect(() => {
        if (formSubmitted) {
            setHeader(
                intl.formatMessage(stepMessages.creationInProgressHeader)
            );
            setSubheader(
                intl.formatMessage(stepMessages.creationInProgressSubheader)
            );
        }
    }, [formSubmitted, intl]);

    // update header and subheader when project setup is complete and move to next step
    useEffect(() => {
        if (projectSetupComplete) {
            setHeader(intl.formatMessage(stepMessages.creationCompleteHeader));
            setSubheader(
                intl.formatMessage(stepMessages.creationCompleteSubheader)
            );

            // move to next step after 2 seconds
            setTimeout(() => {
                nextStep();
            }, 2_000);
        }
    }, [projectSetupComplete, intl, nextStep]);

    return (
        <div css={container}>
            <h1 css={[headerCss]}>{header}</h1>
            <p css={[subheaderCss]}>{subheader}</p>
            {formSubmitted ? (
                <ProgressBar
                    data-testid="progress-bar"
                    isIndeterminate={!projectSetupComplete}
                    value={100}
                    appearance={projectSetupComplete ? "success" : undefined}
                />
            ) : (
                <MadlibSetupPrompt
                    onSubmit={onSubmit}
                    defaultValue={defaultValue}
                    suggestions={suggestions}
                    ActionFooterComponent={ActionFooterComponent}
                />
            )}
        </div>
    );
};

const ActionFooterComponent = () => {
    const intl = useIntl();

    const onSkipButtonClick = () => {
        // TODO: create project with basic issues
    };

    return (
        <div css={css({ display: "flex", gap: 8 })}>
            <Button
                appearance="subtle"
                testId="skip-button"
                onClick={onSkipButtonClick}
            >
                {intl.formatMessage(stepMessages.skipButton)}
            </Button>
            <Button appearance="primary" testId="submit-button" type="submit">
                {intl.formatMessage(stepMessages.submitButton)}
            </Button>
        </div>
    );
};

const container = css({
    minHeight: 400
});

const headerCss = css({
    fontSize: 28,
    fontWeight: 700,
    margin: "24px 0 0"
});

const subheaderCss = css({
    margin: "8px 0 24px",
    color: token("color.text.subtle", "#6B778C")
});
